import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MergePanelGroup, MergePanelMember } from './merge-panel.model';

/**
 * A side panel showing what a Lab Manager sample merge contains.
 *
 * Merging hides samples. In Lab Manager linked samples are excluded from the
 * list; in Recommendation Manager the group collapses to one row when the
 * recommendation is created. Both are correct and neither was visible, so a
 * user saw fewer samples than they submitted with nothing to explain it. This
 * is where the hidden ones can be read.
 *
 * Follows the Pin Manager panel: fixed to the right, slid in with a transform,
 * the host screen shifting its content with a margin. There is no mat-sidenav
 * anywhere in this app and this is not the place to introduce one.
 *
 * Read only unless the host screen says otherwise. Lab Manager allows a single
 * sample to be taken out of a group here, while the submission is still
 * editable; Recommendation Manager never does, because a merge belongs to the
 * submission rather than to the recommendation reading it.
 */
@Component({
  selector: 'app-merge-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './merge-panel.component.html'
})
export class MergePanelComponent implements OnChanges {

  @Input() open = false;

  /** Null renders an empty panel, which only shows while it slides out. */
  @Input() group: MergePanelGroup | null = null;

  /**
   * Whether a member can be taken out of the group from here.
   *
   * Off by default, so a screen has to opt in. Lab Manager turns it on only
   * while the submission is still editable: once it has gone to the lab the
   * results have been copied onto every member, and freeing one would leave it
   * carrying values that were never measured for it. The API refuses it there
   * regardless, this only keeps the button from appearing.
   */
  @Input() canUnmerge = false;

  @Output() closed = new EventEmitter<void>();

  /**
   * A request to take one sample out of the group. The host does the work and
   * reloads, because it owns the list this panel was opened from.
   */
  @Output() unmergeMember = new EventEmitter<MergePanelMember>();

  /** Set while a removal is in flight, so a double click cannot send two. */
  pending: number | null = null;

  /**
   * Clears the in-flight guard whenever the group is replaced or the panel is
   * closed.
   *
   * The host rebuilds the group from the server after a removal, so a new group
   * object arriving IS the completion signal. Without this the guard would stay
   * set and the first removal would be the only one allowed. Cleared on failure
   * too, because the host closes the panel when the call errors.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] || changes['open']) {
      this.pending = null;
    }
  }

  removeMember(member: MergePanelMember): void {
    if (!this.canUnmerge || this.pending != null || member.isPrimary) {
      return;
    }
    this.pending = member.sampleId;
    this.unmergeMember.emit(member);
  }
}
